/**
 * Grid Snapping Utility
 * Centralized logic for snapping times to the beat grid
 */

/**
 * Calculate the duration of one beat in seconds
 * @param {number} bpm - Beats per minute
 * @returns {number} Duration of one beat in seconds
 */
export const getBeatDuration = (bpm) => {
  return 60 / bpm;
};

/**
 * Calculate the duration of one bar/measure in seconds
 * @param {number} bpm - Beats per minute
 * @param {number} timeSignature - Beats per measure (e.g., 4 for 4/4)
 * @returns {number} Duration of one bar in seconds
 */
export const getBarDuration = (bpm, timeSignature = 4) => {
  return getBeatDuration(bpm) * timeSignature;
};

/**
 * Snap a time value to the nearest beat
 * @param {number} time - Time in seconds to snap
 * @param {number} bpm - Beats per minute
 * @param {number} timeSignature - Beats per measure (default: 4)
 * @returns {number} Snapped time in seconds
 */
export const snapToGrid = (time, bpm, timeSignature = 4) => {
  if (time < 0) return 0;
  
  const beatDuration = getBeatDuration(bpm);
  
  // Snap to nearest beat (quarter note)
  const beatIndex = Math.round(time / beatDuration);
  const snappedTime = beatIndex * beatDuration;
  
  // Ensure non-negative
  return Math.max(0, snappedTime);
};

/**
 * Snap a time value to the nearest subdivision
 * @param {number} time - Time in seconds to snap
 * @param {number} bpm - Beats per minute
 * @param {string} subdivision - 'bar', 'beat', 'half', 'quarter', 'eighth', 'sixteenth'
 * @param {number} timeSignature - Beats per measure (default: 4)
 * @returns {number} Snapped time in seconds
 */
export const snapToSubdivision = (time, bpm, subdivision = 'beat', timeSignature = 4) => {
  if (time < 0) return 0;
  
  const beatDuration = getBeatDuration(bpm);
  let gridSize;
  
  switch (subdivision) {
    case 'bar':
      gridSize = beatDuration * timeSignature;
      break;
    case 'half':
      gridSize = beatDuration * 2;
      break;
    case 'quarter':
    case 'beat':
      gridSize = beatDuration;
      break;
    case 'eighth':
      gridSize = beatDuration / 2;
      break;
    case 'sixteenth':
      gridSize = beatDuration / 4;
      break;
    default:
      gridSize = beatDuration;
  }
  
  const gridIndex = Math.round(time / gridSize);
  return Math.max(0, gridIndex * gridSize);
};

/**
 * Get the grid lines for a given time range
 * @param {number} duration - Total duration in seconds
 * @param {number} bpm - Beats per minute
 * @param {number} timeSignature - Beats per measure
 * @returns {Array} Array of { time, isBar } objects
 */
export const getGridLines = (duration, bpm, timeSignature = 4) => {
  const beatDuration = getBeatDuration(bpm);
  const lines = [];
  
  for (let beat = 0; beat * beatDuration <= duration; beat++) {
    lines.push({
      time: beat * beatDuration,
      isBar: beat % timeSignature === 0,
      beat: beat % timeSignature,
      bar: Math.floor(beat / timeSignature),
    });
  }
  
  return lines;
};

/**
 * Conditionally snap time based on snap enabled flag
 * @param {number} time - Time in seconds
 * @param {boolean} snapEnabled - Whether snapping is enabled
 * @param {number} bpm - Beats per minute
 * @param {number} timeSignature - Beats per measure
 * @returns {number} Snapped or original time
 */
export const conditionalSnap = (time, snapEnabled, bpm, timeSignature = 4) => {
  if (!snapEnabled) return time;
  return snapToGrid(time, bpm, timeSignature);
};

export default {
  getBeatDuration,
  getBarDuration,
  snapToGrid,
  snapToSubdivision,
  getGridLines,
  conditionalSnap,
};
